/**
 * SecuritySettingsSection — Admin Portal "Security" tab.
 *
 * Phase 3.0 surfaces a single informational toggle:
 *   us_only_ip_restriction  — persists admin intent but NO middleware
 *                              enforces it yet. Tooltip + helper copy
 *                              make this explicit so admins aren't
 *                              misled about coverage.
 *
 * Backed by `PATCH /api/config/pricing` with the `security` subdoc. The
 * server does a deep-merge so other security keys (set directly by ops)
 * are preserved.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useConfig } from "@/context/ConfigContext";
import { ShieldAlert, Loader2 } from "lucide-react";
import AdminsAndInvitesSection from "@/components/admin/AdminsAndInvitesSection";

export default function SecuritySettingsSection() {
  const { security, refresh } = useConfig();
  const [usOnly, setUsOnly] = useState(!!security?.us_only_ip_restriction);
  const [saving, setSaving] = useState(false);

  // Re-sync local state if the context refreshes while we're mounted.
  useEffect(() => {
    setUsOnly(!!security?.us_only_ip_restriction);
  }, [security?.us_only_ip_restriction]);

  const toggle = async (next) => {
    setSaving(true);
    // Optimistic — flip immediately, roll back on error.
    setUsOnly(next);
    try {
      await api.patch("/config/pricing", {
        security: { us_only_ip_restriction: !!next },
      });
      await refresh();
      toast.success(
        next
          ? "US-only IP restriction enabled (informational — enforcement coming in a later phase)."
          : "US-only IP restriction disabled."
      );
    } catch (e) {
      setUsOnly(!next);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-2" data-testid="security-section">
      <header className="mb-5">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-6 w-6 text-brand-orange" aria-hidden="true" />
          <h2 className="font-heading text-2xl font-semibold text-pp-text">Security</h2>
        </div>
        <p className="mt-1 text-sm text-pp-text-muted">
          Account &amp; access controls. New toggles ship here as we expand
          enforcement coverage.
        </p>
      </header>

      <Card
        className="rounded-2xl border border-pp-border bg-pp-surface p-6"
        style={{ boxShadow: "var(--pp-card-shadow)" }}
        data-testid="security-card"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Label className="text-base font-semibold text-pp-text">
              US-only IP restriction
            </Label>
            <p className="mt-1 text-sm text-pp-text-muted">
              When enabled, only requests originating from US IP addresses will
              be served. Useful for compliance and abuse mitigation.
            </p>
            <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
              <ShieldAlert className="h-3 w-3" /> Informational only — enforcement coming soon
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
              {usOnly ? "On" : "Off"}
            </span>
            <Switch
              checked={usOnly}
              onCheckedChange={toggle}
              disabled={saving}
              data-testid="security-us-only-toggle"
              aria-label="US-only IP restriction"
            />
            {saving && (
              <Loader2 className="h-4 w-4 animate-spin text-pp-text-muted" aria-hidden="true" />
            )}
          </div>
        </div>
      </Card>

      {/* Admin Portal Consolidation Step 4 — Admins & Invites lives
          inside the Security tab below the US-only IP toggle. */}
      <AdminsAndInvitesSection />
    </section>
  );
}
